import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  PackageOpen,
  Scissors,
  Truck,
  Users,
  ArrowRight,
  Edit,
  Plus,
  DollarSign,
  Ticket,
  Printer,
  Grid,
  Trash2,
} from "lucide-react";
import { CustomerNotesModal } from "./CustomerNotesModal";
import { AddPrepaidModal } from "./AddPrepaidModal";
import { PrepaidListModal } from "./PrepaidListModal";
import { handlePrepaidDeduction, getPrepaidSummary, refundPrepaidDeduction } from "../utils/prepaidUtils";

const API_URL =
  import.meta.env.VITE_API_URL || "https://techtennis-api.vercel.app";
const getAuthHeader = () => {
  const token = localStorage.getItem("tt_auth_token");
  return { Authorization: `Bearer ${token}` };
};

export const OrderDetailsView = ({
  view,
  setView,
  activeOrderJob,
  jobs,
  setJobs,
  setActiveStringingJob,
  setActivePaymentJob,
  setIsPaymentModalOpen,
  customers,
  setSelectedCustomer,
  setNewJobStep,
  setActiveFilter,
  setIsCustomerModalOpen,
  startEditingJob,
  setCurrentOrderCode,
  resetForm,
  fetchCustomers,
  rackets,
}: any) => {
  const getNextDay = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 16);
  };

  const [isEditingPickup, setIsEditingPickup] = useState(false);
  const [pickupDate, setPickupDate] = useState(
    activeOrderJob?.pickupDate || getNextDay(),
  );
  const [pickupNotes, setPickupNotes] = useState(
    activeOrderJob?.pickupNotes || "",
  );
  const isSameOrder = (j: any) => {
    const targetCode = activeOrderJob.orderCode || activeOrderJob.id;
    return (
      j.orderCode === targetCode ||
      j.id === activeOrderJob.id ||
      (j.orderCode === activeOrderJob.orderCode && activeOrderJob.orderCode)
    );
  };
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [customerNotes, setCustomerNotes] = useState<
    { id: string; text: string; date: string }[]
  >([]);
  const [isAddPrepaidModalOpen, setIsAddPrepaidModalOpen] = useState(false);
  const [isPrepaidListModalOpen, setIsPrepaidListModalOpen] = useState(false);

  useEffect(() => {
    if (activeOrderJob) {
      setPickupDate(activeOrderJob.pickupDate || getNextDay());
      setPickupNotes(activeOrderJob.pickupNotes || "");
      setIsEditingPickup(false);

      const savedNotes = localStorage.getItem(
        "tt_customer_notes_" + activeOrderJob.customerName,
      );
      setCustomerNotes(savedNotes ? JSON.parse(savedNotes) : []);
    }
  }, [activeOrderJob]);

  if (view !== "order_details" || !activeOrderJob) return null;

  const activeCustomer = customers?.find((c: any) => 
    (activeOrderJob.customerId && (c.id === activeOrderJob.customerId || c.numericId?.toString() === activeOrderJob.customerId?.toString())) ||
    (c.name || "").trim().toLowerCase() === (activeOrderJob.customerName || "").trim().toLowerCase() ||
    (c.name || "").trim().toLowerCase().startsWith((activeOrderJob.customerName || "").trim().toLowerCase())
  );
  const orderJobs = jobs.filter(isSameOrder);
  const totalJobs = orderJobs.length;
  const completedJobs = orderJobs.filter(
    (j: any) => j.type === "picking_up",
  ).length;
  const progressPercent =
    totalJobs > 0 ? Math.round((completedJobs / totalJobs) * 100) : 0;
  const isFullyPaid = !orderJobs.some((j: any) => !j.paid);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      style={{
        width: "100%",
        maxWidth: "1200px",
        display: "flex",
        flexDirection: "column",
        gap: "24px",
      }}
    >
      {/* Top Status Header */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "16px",
        }}
      >
        <button
          onClick={() => {
            setView("new_job");
            setNewJobStep(1);
          }}
          style={{
            background: "#D93B65",
            color: "white",
            border: "none",
            padding: "16px 24px",
            borderRadius: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
          }}
        >
          <PackageOpen size={20} /> Recebimento
        </button>
        <button
          onClick={() => {
            setView("dashboard");
            setActiveFilter("to_string");
          }}
          style={{
            background: "#F2C94C",
            color: "var(--text-dark)",
            border: "none",
            padding: "16px 24px",
            borderRadius: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
          }}
        >
          <Scissors size={20} /> Para Encordoar:{" "}
          {jobs.filter((j: any) => j.type === "to_string").length}
        </button>
        <button
          onClick={() => {
            setView("dashboard");
            setActiveFilter("picking_up");
          }}
          style={{
            background: "#6FCF97",
            color: "var(--text-dark)",
            border: "none",
            padding: "16px 24px",
            borderRadius: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
          }}
        >
          <Truck size={20} /> Para Retirar:{" "}
          {jobs.filter((j: any) => j.type === "picking_up").length}
        </button>
        <button
          onClick={() => setView("customers")}
          style={{
            background: "#9B51E0",
            color: "white",
            border: "none",
            padding: "16px 24px",
            borderRadius: "12px",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
          }}
        >
          <Users size={20} /> Clientes
        </button>
      </div>

      {/* Quick Actions Row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.2fr 1.2fr 1fr",
          gap: "16px",
        }}
      >
        <div
          className="glass-panel"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            background:
              customerNotes.length > 0
                ? "rgba(155, 81, 224, 0.2)"
                : "rgba(155, 81, 224, 0.1)",
          }}
        >
          {customerNotes.length > 0 ? (
            <div style={{ color: "#5C3A82" }}>
              <div style={{ fontSize: "15px", fontWeight: 600 }}>
                {customerNotes[customerNotes.length - 1].text.substring(0, 30)}
                {customerNotes[customerNotes.length - 1].text.length > 30
                  ? "..."
                  : ""}
              </div>
              <div style={{ fontSize: "15px" }}>
                ({customerNotes[customerNotes.length - 1].date})
              </div>
            </div>
          ) : (
            <span
              style={{ color: "#C08DF8", fontWeight: 600, fontSize: "15px" }}
            >
              Sem notas para o cliente
            </span>
          )}
          <button
            onClick={() => setIsNotesModalOpen(true)}
            style={{
              background: "#7C3AED",
              border: "none",
              padding: "10px 24px",
              borderRadius: "8px",
              color: "white",
              fontWeight: 700,
              cursor: "pointer",
              fontSize: "16px",
            }}
          >
            Notas {customerNotes.length > 0 ? `(${customerNotes.length})` : ""}
          </button>
        </div>
        <div
          className="glass-panel"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
            background: "rgba(66, 152, 231, 0.1)",
          }}
        >
          <span style={{ color: "#7EBDF7", fontWeight: 600, fontSize: "15px" }}>
            {(() => {
              const { availableStringings, availableAmount, currentPrepaid } = getPrepaidSummary(activeCustomer);
              if (availableStringings > 0 && availableAmount > 0) {
                return `Saldo: ${availableStringings} encord. / R$ ${availableAmount.toFixed(2)}`;
              } else if (availableStringings > 0) {
                return `Saldo: ${availableStringings} encord.`;
              } else if (availableAmount > 0) {
                return `Saldo: R$ ${availableAmount.toFixed(2)}`;
              }
              if (currentPrepaid && currentPrepaid.length > 0) {
                return "Saldo Esgotado";
              }
              return "Sem pré-pago para o cliente";
            })()}
          </span>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => setIsAddPrepaidModalOpen(true)}
              style={{
                background: "#4298E7",
                border: "none",
                width: "36px",
                height: "36px",
                borderRadius: "8px",
                color: "white",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              +
            </button>
            <button
              onClick={() => setIsPrepaidListModalOpen(true)}
              style={{
                background: "#4298E7",
                border: "none",
                padding: "0 20px",
                height: "36px",
                borderRadius: "8px",
                color: "white",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Pré-pago
            </button>
          </div>
        </div>
        <div
          className="glass-panel"
          style={{
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            padding: "16px 24px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontWeight: 700, fontSize: "15px" }}>
              Rolo próprio
            </span>{" "}
            <input
              type="checkbox"
              checked={activeOrderJob?.hasOwnReel || false}
              onChange={(e) => {
                const val = e.target.checked;
                setJobs(
                  jobs.map((j: any) =>
                    j.id === activeOrderJob.id ? { ...j, hasOwnReel: val } : j,
                  ),
                );
                fetch(`${API_URL}/api/jobs/${activeOrderJob.id}`, {
                  method: "PUT",
                  headers: {
                    ...getAuthHeader(),
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ ...activeOrderJob, hasOwnReel: val }),
                }).catch(console.error);
              }}
              style={{ accentColor: "#D93B65", width: "22px", height: "22px" }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontWeight: 700, fontSize: "15px" }}>
              Set próprio
            </span>{" "}
            <input
              type="checkbox"
              checked={activeOrderJob?.hasOwnSet || false}
              onChange={(e) => {
                const val = e.target.checked;
                setJobs(
                  jobs.map((j: any) =>
                    j.id === activeOrderJob.id ? { ...j, hasOwnSet: val } : j,
                  ),
                );
                fetch(`${API_URL}/api/jobs/${activeOrderJob.id}`, {
                  method: "PUT",
                  headers: {
                    ...getAuthHeader(),
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ ...activeOrderJob, hasOwnSet: val }),
                }).catch(console.error);
              }}
              style={{ accentColor: "#D93B65", width: "22px", height: "22px" }}
            />
          </div>
        </div>
      </div>

      {/* Main Order Info */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)",
          gap: "24px",
        }}
      >
        {/* Left Order Info Panel */}
        <div
          className="glass-panel"
          style={{
            padding: "32px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            background: "var(--bg-panel)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: 800,
                  margin: "0 0 12px 0",
                }}
              >
                {activeOrderJob.customerName}
              </h2>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "var(--text-secondary)",
                  fontWeight: 600,
                }}
              >
                <ArrowRight size={16} strokeWidth={3} />{" "}
                {activeCustomer?.originClub || "Não informado"}
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={() => {
                  if (activeCustomer) {
                    setSelectedCustomer(activeCustomer);
                    setIsCustomerModalOpen(true);
                  }
                }}
                style={{
                  background: "#6136B3",
                  border: "none",
                  padding: "12px 20px",
                  borderRadius: "8px",
                  color: "white",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                }}
              >
                <Users size={18} /> Perfil do cliente
              </button>
              {!isFullyPaid ? (
                <button
                  onClick={async () => {
                    const targetJobs = jobs.filter((j: any) => isSameOrder(j));
                    
                    const { continueWithManualPayment, jobsToMarkPaid, aborted } = await handlePrepaidDeduction(customers, activeOrderJob.customerName, targetJobs, API_URL, getAuthHeader, activeOrderJob.customerId);

                    if (aborted) return;
                    if (fetchCustomers) await fetchCustomers();

                                        let jobsToActuallyMarkPaid = jobsToMarkPaid;
                    if (continueWithManualPayment) {
                        jobsToActuallyMarkPaid = targetJobs;
                    }

                    const isPrepaid = !continueWithManualPayment && jobsToActuallyMarkPaid.length > 0;

                    setJobs(
                      jobs.map((j: any) =>
                        jobsToActuallyMarkPaid.some((tj: any) => tj.id === j.id) 
                          ? { 
                              ...j, 
                              paid: true,
                              originalData: isPrepaid ? (typeof j.originalData === 'string' ? JSON.stringify({ ...JSON.parse(j.originalData || '{}'), paymentMethod: 'PREPAID' }) : { ...(j.originalData || {}), paymentMethod: 'PREPAID' }) : j.originalData
                            } 
                          : j,
                      ),
                    );
                    for (let j of jobsToActuallyMarkPaid) {
                      if (!j.paid) {
                        const originalData = typeof j.originalData === 'string' ? JSON.parse(j.originalData || '{}') : (j.originalData || {});
                        if (isPrepaid) originalData.paymentMethod = 'PREPAID';
                        
                        fetch(`${API_URL}/api/jobs/${j.id}/status`, {
                          method: "PUT",
                          headers: {
                            ...getAuthHeader(),
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({ paid: true, originalData: isPrepaid ? JSON.stringify(originalData) : undefined }),
                        }).catch(console.error);
                      }
                    }
                  }}
                  style={{
                    background: "#6FCF97",
                    border: "none",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    color: "var(--text-dark)",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Marcar como pago
                </button>
              ) : (
                                <button
                  onClick={async () => {
                    const targetJobs = jobs.filter((j: any) => isSameOrder(j));
                    await refundPrepaidDeduction(customers, targetJobs, API_URL, getAuthHeader);
                    if (fetchCustomers) await fetchCustomers();

                    setJobs(
                      jobs.map((j: any) =>
                        isSameOrder(j) ? { ...j, paid: false, originalData: typeof j.originalData === 'string' ? JSON.stringify({ ...JSON.parse(j.originalData || '{}'), paymentMethod: null }) : { ...(j.originalData || {}), paymentMethod: null } } : j,
                      ),
                    );
                    for (let j of jobs) {
                      if (isSameOrder(j) && j.paid) {
                        const originalData = typeof j.originalData === 'string' ? JSON.parse(j.originalData || '{}') : (j.originalData || {});
                        originalData.paymentMethod = null;
                        const newOriginalData = JSON.stringify(originalData);
                        fetch(`${API_URL}/api/jobs/${j.id}/status`, {
                          method: "PUT",
                          headers: {
                            ...getAuthHeader(),
                            "Content-Type": "application/json",
                          },
                          body: JSON.stringify({ paid: false, originalData: newOriginalData }),
                        }).catch(console.error);
                      }
                    }
                  }}
                  style={{
                    background: "#D93B65",
                    border: "none",
                    padding: "12px 20px",
                    borderRadius: "8px",
                    color: "white",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Cancelar pagamento
                </button>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "24px",
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Data de entrada{" "}
                <Edit
                  size={12}
                  style={{ marginLeft: "4px", cursor: "pointer" }}
                />
              </div>
              <div
                style={{
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#C08DF8",
                  background: "rgba(155,81,224,0.1)",
                  padding: "6px 16px",
                  borderRadius: "100px",
                  display: "inline-block",
                }}
              >
                {activeOrderJob.date}
              </div>
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Status
              </div>
              <div
                style={{ fontSize: "15px", fontWeight: 800, color: "#7EBDF7" }}
              >
                {activeOrderJob.type === "picking_up"
                  ? "PRONTA"
                  : activeOrderJob.type === "to_string"
                    ? "PARA ENCORDOAR"
                    : activeOrderJob.status.toUpperCase()}
              </div>
            </div>
            <div style={{ flex: 1.5 }}>
              <div
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Progresso
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div style={{ fontWeight: 800, fontSize: "16px" }}>
                  {progressPercent}%
                </div>
                <div
                  style={{
                    flex: 1,
                    height: "10px",
                    background: "rgba(255,255,255,0.05)",
                    borderRadius: "5px",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <div
                    style={{
                      width: `${progressPercent}%`,
                      height: "100%",
                      background:
                        progressPercent === 100 ? "#6FCF97" : "#DDE83D",
                      borderRadius: "5px",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              marginTop: "32px",
            }}
          >
            <div
              style={{
                background: "rgba(66,152,231,0.1)",
                border: "1px solid rgba(66,152,231,0.2)",
                padding: "20px",
                borderRadius: "12px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  color: "#7EBDF7",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Ponto de encordoamento
              </div>
              <div style={{ fontWeight: 800, fontSize: "18px" }}>
                {activeOrderJob.stringingPoint ||
                  activeCustomer?.stringingPoint ||
                  "Não definido"}
              </div>
            </div>
            <div
              style={{
                background: "rgba(217,59,101,0.1)",
                border: "1px solid rgba(217,59,101,0.2)",
                padding: "20px",
                borderRadius: "12px",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  color: "#F9D0DA",
                  marginBottom: "8px",
                  fontWeight: 600,
                }}
              >
                Código da ordem
              </div>
              <div style={{ fontWeight: 800, fontSize: "18px" }}>
                {activeOrderJob.orderCode ||
                  activeOrderJob.id?.substring(0, 8).toUpperCase() ||
                  "2E0Z8HK5"}
              </div>
            </div>
          </div>
        </div>

        {/* Right Dropoff info Panel */}
        <div
          className="glass-panel"
          style={{
            padding: "32px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            background: "#D9DCDF",
            color: "var(--text-dark)",
          }}
        >
          <h3
            style={{ fontSize: "24px", fontWeight: 900, margin: "0 0 16px 0" }}
          >
            Previsão de Retirada
          </h3>

          <div>
            <div
              style={{
                fontSize: "14px",
                color: "#555",
                marginBottom: "8px",
                fontWeight: 600,
              }}
            >
              Data de retirada
            </div>
            {isEditingPickup ? (
              <input
                type="datetime-local"
                min={new Date(
                  Date.now() - new Date().getTimezoneOffset() * 60000,
                )
                  .toISOString()
                  .slice(0, 16)}
                value={pickupDate}
                onChange={(e) => setPickupDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  fontSize: "16px",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  background: "#FFFFFF",
                  color: "#333",
                }}
              />
            ) : (
              <div style={{ fontSize: "20px", fontWeight: 800 }}>
                {new Date(pickupDate)
                  .toLocaleDateString("pt-BR", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                  .replace(/^\w/, (c) => c.toUpperCase())}
              </div>
            )}
          </div>

          <div style={{ marginTop: "auto" }}>
            <div
              style={{
                fontSize: "14px",
                color: "#555",
                marginBottom: "8px",
                fontWeight: 600,
              }}
            >
              Observações
            </div>
            <textarea
              readOnly={!isEditingPickup}
              value={pickupNotes}
              onChange={(e) => setPickupNotes(e.target.value)}
              placeholder={
                isEditingPickup ? "Insira as observações aqui..." : ""
              }
              style={{
                width: "100%",
                padding: "16px",
                borderRadius: "12px",
                border: isEditingPickup ? "2px solid #111" : "1px solid #ccc",
                background: isEditingPickup ? "#FFFFFF" : "#F8F9FA",
                color: "#333",
                resize: "none",
                minHeight: "120px",
              }}
            ></textarea>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "16px",
            }}
          >
            {isEditingPickup ? (
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => {
                    setIsEditingPickup(false);
                    setPickupDate(activeOrderJob?.pickupDate || pickupDate);
                    setPickupNotes(activeOrderJob?.pickupNotes || pickupNotes);
                  }}
                  style={{
                    background: "transparent",
                    color: "#555",
                    border: "1px solid #999",
                    padding: "14px 24px",
                    borderRadius: "8px",
                    fontWeight: 700,
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    setIsEditingPickup(false);
                    // Update all jobs for this customer with the new pickup details
                    const updatedJobs = jobs.map((j: any) =>
                      isSameOrder(j) ? { ...j, pickupDate, pickupNotes } : j,
                    );
                    setJobs(updatedJobs);

                    // Update backend for all related jobs
                    jobs.filter(isSameOrder).forEach((j: any) => {
                      fetch(`${API_URL}/api/jobs/${j.id}`, {
                        method: "PUT",
                        headers: {
                          ...getAuthHeader(),
                          "Content-Type": "application/json",
                        },
                        body: JSON.stringify({ ...j, pickupDate, pickupNotes }),
                      }).catch(console.error);
                    });
                  }}
                  style={{
                    background: "#111",
                    color: "white",
                    border: "none",
                    padding: "14px 40px",
                    borderRadius: "8px",
                    fontWeight: 700,
                    fontSize: "16px",
                    cursor: "pointer",
                  }}
                >
                  Salvar alterações
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingPickup(true)}
                style={{
                  background: "#111",
                  color: "white",
                  border: "none",
                  padding: "14px 40px",
                  borderRadius: "8px",
                  fontWeight: 700,
                  fontSize: "16px",
                  cursor: "pointer",
                }}
              >
                Editar
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Rackets to string bottom table */}
      <div
        className="glass-panel"
        style={{
          padding: "32px",
          background: "white",
          color: "var(--text-dark)",
          overflowX: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "32px",
          }}
        >
          <h3 style={{ fontSize: "24px", fontWeight: 900, margin: 0 }}>
            Raquetes da ordem
          </h3>
          <button
            onClick={() => {
              if (resetForm) resetForm();
              const cust = customers?.find(
                (c: any) => (c.name || "").trim().toLowerCase() === (activeOrderJob.customerName || "").trim().toLowerCase() || (c.name || "").trim().toLowerCase().startsWith((activeOrderJob.customerName || "").trim().toLowerCase()),
              );
              if (cust) {
                setSelectedCustomer(cust);
              }
              if (setCurrentOrderCode) {
                const codeToUse = activeOrderJob.orderCode || activeOrderJob.id;
                setCurrentOrderCode(codeToUse);

                // If the active job didn't have an orderCode, we should update it so they group together
                if (!activeOrderJob.orderCode) {
                  const updatedJob = {
                    ...activeOrderJob,
                    orderCode: codeToUse,
                  };
                  setJobs((prev: any) =>
                    prev.map((j: any) =>
                      j.id === activeOrderJob.id ? updatedJob : j,
                    ),
                  );
                  fetch(`${API_URL}/api/jobs/${activeOrderJob.id}`, {
                    method: "PUT",
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("tt_auth_token")}`,
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ orderCode: codeToUse }),
                  }).catch(console.error);
                }
              }
              setNewJobStep(2);
              setView("new_job");
            }}
            style={{
              background: "#D93B65",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontWeight: 700,
              fontSize: "15px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <Plus size={18} strokeWidth={3} /> Adicionar raquete
          </button>
        </div>

        {/* Table Header */}
        <div
          style={{
            minWidth: "1000px",
            display: "grid",
            gridTemplateColumns:
              "minmax(220px, 1.5fr) 1fr 1fr 1fr 1fr 1fr 1fr 240px",
            fontSize: "14px",
            color: "#666",
            fontWeight: 700,
            paddingBottom: "16px",
            borderBottom: "1px solid #E5E7EB",
            marginBottom: "16px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            Raquete <span style={{ fontSize: "10px", color: "#aaa" }}>▼</span>
          </div>
          <div>Mains</div>
          <div>Crosses</div>
          <div>Status</div>
          <div>Encordoador</div>
          <div>Data do encord.</div>
          <div>Preço</div>
          <div style={{ textAlign: "center" }}>Ações</div>
        </div>

        {/* Table Rows corresponding to Order */}
        {jobs.filter(isSameOrder).map((orderJob: any) => {
          const getRacketSuffix = () => {
            if (!rackets || !orderJob.racketId) return "";
            const r = rackets.find((rk: any) => rk.id === orderJob.racketId);
            if (!r) return "";
            return r.identifier ? ` [${r.identifier}]` : "";
          };

          const suffix = getRacketSuffix();

          return (
            <div
              key={orderJob.id}
              style={{
                minWidth: "1000px",
                display: "grid",
                gridTemplateColumns:
                  "minmax(220px, 1.5fr) 1fr 1fr 1fr 1fr 1fr 1fr 240px",
                alignItems: "center",
                padding: "24px 0",
                borderBottom: "1px solid #F3F4F6",
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: "16px",
                    marginBottom: "4px",
                  }}
                >
                  {orderJob.racketModel}
                  {suffix && (
                    <span style={{ fontWeight: 600, color: "#888" }}>
                      {" "}
                      {suffix}
                    </span>
                  )}
                </div>
                <div
                  style={{ fontSize: "14px", color: "#888", fontWeight: 500 }}
                >
                  {(() => {
                    const r = rackets?.find(
                      (rk: any) => rk.id === orderJob.racketId,
                    );
                    if (!r) return "";
                    return [r.stringPattern, r.gripSize]
                      .filter(Boolean)
                      .join(" ");
                  })()}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "15px",
                    marginBottom: "6px",
                  }}
                >
                  {orderJob.stringMains ||
                    orderJob.mainString ||
                    "Não definido"}
                </div>
                <div
                  style={{
                    display: "inline-block",
                    background: "#4298E7",
                    color: "white",
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: 800,
                  }}
                >
                  @ {orderJob.tensionMain || orderJob.tension || "?"}{" "}
                  {orderJob.tensionUnit || "Lbs"}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: "15px",
                    marginBottom: "6px",
                  }}
                >
                  {orderJob.isHybrid
                    ? orderJob.stringCross ||
                      orderJob.crossString ||
                      orderJob.stringMains ||
                      orderJob.mainString ||
                      "Não definido"
                    : orderJob.stringMains ||
                      orderJob.mainString ||
                      "Não definido"}
                </div>
                <div
                  style={{
                    display: "inline-block",
                    background: "#4298E7",
                    color: "white",
                    padding: "4px 10px",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: 800,
                  }}
                >
                  @{" "}
                  {orderJob.isHybrid
                    ? orderJob.tensionCross ||
                      orderJob.tensionMain ||
                      orderJob.tension ||
                      "?"
                    : orderJob.tensionMain || orderJob.tension || "?"}{" "}
                  {orderJob.tensionUnit || "Lbs"}
                </div>
              </div>
              <div>
                <div
                  style={{
                    display: "inline-block",
                    background: "#F2C94C",
                    color: "var(--text-dark)",
                    padding: "6px 16px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    fontWeight: 800,
                    textTransform: "capitalize",
                  }}
                >
                  {orderJob.status ||
                    (orderJob.type === "to_string"
                      ? "Para Encordoar"
                      : orderJob.type === "picking_up"
                        ? "Pronto"
                        : "Aguardando")}
                </div>
              </div>
              <div style={{ fontWeight: 600 }}>
                {orderJob.stringerName || "-"}
              </div>
              <div style={{ fontSize: "14px", color: "#666" }}>
                {orderJob.updatedAt
                  ? new Date(orderJob.updatedAt).toLocaleDateString("pt-BR")
                  : "-"}
              </div>
              <div style={{ fontWeight: 900, fontSize: "16px" }}>
                {orderJob.price != null && orderJob.price !== "" ? Number(orderJob.price).toFixed(2) : "120.00"} BRL
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "6px",
                  justifyContent: "center",
                }}
              >
                <button
                  onClick={() => {
                    setActivePaymentJob(orderJob);
                    setIsPaymentModalOpen(true);
                  }}
                  style={{
                    background: orderJob.paid ? "#6FCF97" : "#D93B65",
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  title="Pagamento"
                >
                  <DollarSign size={16} strokeWidth={3} />
                </button>
                <button
                  onClick={() => {
                    import("../utils/printUtils").then((m) =>
                      m.printLabel(
                        orderJob,
                        "heart",
                        rackets?.find((r: any) => r.id === orderJob.racketId),
                      ),
                    );
                  }}
                  style={{
                    background: "#E5E7EB",
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    color: "#6B7280",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  title="Imprimir Etiqueta Coração"
                >
                  <Ticket size={16} strokeWidth={3} />
                </button>
                <button
                  onClick={() => {
                    import("../utils/printUtils").then((m) =>
                      m.printLabel(
                        orderJob,
                        "full",
                        rackets?.find((r: any) => r.id === orderJob.racketId),
                      ),
                    );
                  }}
                  style={{
                    background: "#E5E7EB",
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    color: "#6B7280",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  title="Imprimir Etiqueta"
                >
                  <Printer size={16} strokeWidth={3} />
                </button>
                <button
                  onClick={() => {
                    if (
                      window.confirm(
                        "Tem certeza que deseja excluir esta raquete?",
                      )
                    ) {
                      // Deleta do TT
                      fetch(`${API_URL}/api/jobs/${orderJob.id}`, {
                        method: "DELETE",
                        headers: getAuthHeader(),
                      })
                        .then(async (res) => {
                          if (!res.ok) {
                            const err = await res.json().catch(() => ({}));
                            throw new Error(err.error || 'Erro ao deletar no servidor');
                          }
                          const remainingJobs = jobs.filter(
                            (j: any) => j.id !== orderJob.id,
                          );
                          setJobs(remainingJobs);

                          setView("dashboard");

                          // Atualiza Bling
                          const oCode = orderJob.orderCode || orderJob.id?.substring(0,8).toUpperCase();
                          if (
                            import.meta.env.VITE_BFF_URL &&
                            oCode
                          ) {
                            const orderJobsBling = remainingJobs.filter(
                              (j: any) =>
                                (j.orderCode || j.id?.substring(0,8).toUpperCase()) === oCode
                            );

                            if (orderJobsBling.length === 0) {
                              // Não tem mais itens, deleta o pedido no Bling
                              fetch(
                                `${import.meta.env.VITE_BFF_URL}/api/pedidos/${oCode}`,
                                { method: "DELETE" },
                              ).catch(console.error);
                            } else {
                              // Refaz o payload do pedido com os itens restantes
                              const orderItems = orderJobsBling.map(
                                (j: any) => {
                                  let desc = "";
                                  if (j.isStringing) {
                                    const mString =
                                      j.stringMains ||
                                      j.mainString ||
                                      "Não definido";
                                    const cString =
                                      j.stringCross || j.crossString || mString;
                                    const str = j.isHybrid
                                      ? `${mString}/${cString}`
                                      : mString;
                                    desc = `Ordem TT: ${oCode} - Corda (${str})`;
                                  } else {
                                    desc = `Ordem TT: ${oCode} - Peso e/ou Grip`;
                                  }
                                  if (desc.length > 120)
                                    desc = desc.substring(0, 117) + "...";
                                  return {
                                    id: j.id,
                                    description: desc,
                                    quantity: 1,
                                    price: Number(j.price) || 0,
                                  };
                                },
                              );
                              const orderTotal = orderJobsBling.reduce(
                                (acc: number, j: any) => acc + (Number(j.price) || 0),
                                0,
                              );

                              const payload: any = {
                                id: oCode,
                                total: orderTotal,
                                items: orderItems,
                              };
                              
                              const custId = orderJobsBling[0].customerId || orderJob.customerId;
                              if (custId) payload.customerId = custId;
                              const cName = orderJobsBling[0].customerName || orderJob.customerName;
                              if (cName) payload.customerName = cName;

                              const cust = customers?.find((c: any) => c.name === cName || c.id === custId);
                              if (cust) {
                                  payload.customerPhone = cust.phone || "";
                                  payload.customerEmail = cust.email || "";
                                  payload.customerDocument = cust.document || "";
                                  payload.customerAddress = cust.address || "";
                                  payload.customerAddressNumber = cust.addressNumber || "";
                                  payload.customerComplement = cust.complement || "";
                                  payload.customerNeighborhood = cust.neighborhood || "";
                                  payload.customerCity = cust.city || "";
                                  payload.customerState = cust.state || "";
                                  payload.customerZipCode = cust.zipCode || "";
                              }

                              fetch(
                                `${import.meta.env.VITE_BFF_URL}/api/pedidos/${oCode}`,
                                {
                                  method: "PUT",
                                  headers: {
                                    "Content-Type": "application/json",
                                  },
                                  body: JSON.stringify(payload),
                                },
                              ).catch(console.error);
                            }
                          }
                        })
                        .catch((err) => {
                          console.error(err);
                          alert("Falha ao excluir a raquete. Verifique a conexão.");
                        });
                    }
                  }}
                  style={{
                    background: "#FCA5A5",
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    color: "#B42D2D",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  title="Excluir"
                >
                  <Trash2 size={16} strokeWidth={3} />
                </button>
                <button
                  onClick={() => {
                    const cust = customers?.find(
                      (c: any) => c.name === orderJob.customerName,
                    );
                    if (startEditingJob) {
                      startEditingJob(orderJob, cust);
                    } else {
                      if (cust) setSelectedCustomer(cust);
                      setNewJobStep(2);
                      setView("new_job");
                    }
                  }}
                  style={{
                    background: "#4298E7",
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  title="Editar"
                >
                  <Edit size={16} strokeWidth={3} />
                </button>
                <button
                  onClick={() => {
                    setActiveStringingJob(orderJob);
                    setView("stringing");
                  }}
                  style={{
                    background: "#F2C94C",
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "6px",
                    color: "var(--text-dark)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                  title="Painel de Encordoamento"
                >
                  <Grid size={16} strokeWidth={3} />
                </button>
              </div>
            </div>
          );
        })}

        {/* Table Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            padding: "24px 0 8px 0",
            alignItems: "flex-start",
            borderTop: "1px solid #E5E7EB",
            marginTop: "16px",
          }}
        >
          <div
            style={{
              fontSize: "15px",
              color: "#666",
              display: "flex",
              gap: "16px",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              Show{" "}
              <select
                style={{
                  padding: "4px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              >
                <option>10</option>
              </select>{" "}
              entries
            </div>
            <span>
              Mostrando 1 a {jobs.filter(isSameOrder).length} de{" "}
              {jobs.filter(isSameOrder).length} registros
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "24px", fontWeight: 900 }}>
                {jobs
                  .filter(isSameOrder)
                  .reduce(
                    (acc: number, curr: any) => acc + (curr.price != null && curr.price !== "" ? Number(curr.price) : 120),
                    0,
                  )
                  .toFixed(2)}{" "}
                BRL
              </div>
            </div>
          </div>
        </div>
      </div>

      <CustomerNotesModal
        isOpen={isNotesModalOpen}
        onClose={() => setIsNotesModalOpen(false)}
        customerName={activeOrderJob.customerName}
        existingNotes={customerNotes}
        onSave={(notes: any) => {
          localStorage.setItem(
            "tt_customer_notes_" + activeOrderJob.customerName,
            JSON.stringify(notes),
          );
          setCustomerNotes(notes);
          setIsNotesModalOpen(false);
        }}
      />

      <AddPrepaidModal
        isOpen={isAddPrepaidModalOpen}
        onClose={() => setIsAddPrepaidModalOpen(false)}
        onApply={(data: any) => {
          const activeC = customers?.find((c: any) => 
            (activeOrderJob.customerId && (c.id === activeOrderJob.customerId || c.numericId?.toString() === activeOrderJob.customerId?.toString())) ||
            (c.name || "").trim().toLowerCase() === (activeOrderJob.customerName || "").trim().toLowerCase() ||
            (c.name || "").trim().toLowerCase().startsWith((activeOrderJob.customerName || "").trim().toLowerCase())
          );
          if (activeC) {
            const currentPrepaid =
              (typeof activeC.prepaid === "string"
                ? JSON.parse(activeC.prepaid)
                : activeC.prepaid) || [];
            const updatedPrepaid = [
              ...currentPrepaid,
              { ...data, date: new Date().toISOString() },
            ];
            activeC.prepaid = updatedPrepaid;
            fetch(`${API_URL}/api/customers/${activeC.id}`, {
              method: "PUT",
              headers: {
                ...getAuthHeader(),
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ ...activeC, prepaid: updatedPrepaid }),
            }).then(async res => {
              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                alert(`Erro ao gravar pré-pago: ${err.error || err.details || res.statusText}`);
              } else {
                alert('Pré-pago gravado com sucesso!');
              }
            }).catch(e => {
              console.error(e);
              alert("Erro de conexão ao gravar pré-pago.");
            });
          }
          setIsAddPrepaidModalOpen(false);
        }}
      />
      <PrepaidListModal
        isOpen={isPrepaidListModalOpen}
        onClose={() => setIsPrepaidListModalOpen(false)}
        activeCustomer={(() => {
          const c = customers?.find((c: any) => 
            (activeOrderJob.customerId && (c.id === activeOrderJob.customerId || c.numericId?.toString() === activeOrderJob.customerId?.toString())) ||
            (c.name || "").trim().toLowerCase() === (activeOrderJob.customerName || "").trim().toLowerCase() ||
            (c.name || "").trim().toLowerCase().startsWith((activeOrderJob.customerName || "").trim().toLowerCase())
          );
          return c;
        })()}
        prepaidJobs={(jobs || []).filter((j: any) => {
          const c = customers?.find((c: any) => 
            (activeOrderJob.customerId && (c.id === activeOrderJob.customerId || c.numericId?.toString() === activeOrderJob.customerId?.toString())) ||
            (c.name || "").trim().toLowerCase() === (activeOrderJob.customerName || "").trim().toLowerCase() ||
            (c.name || "").trim().toLowerCase().startsWith((activeOrderJob.customerName || "").trim().toLowerCase())
          );
          if (!c) return false;
          const matchC = (j.customerId && (j.customerId === c.id || j.customerId === c.numericId?.toString())) || (j.customerName || "").trim().toLowerCase() === (c.name || "").trim().toLowerCase();
          if (!matchC) return false;
          const originalData = typeof j.originalData === "string" ? JSON.parse(j.originalData || '{}') : (j.originalData || {});
          return originalData.paymentMethod === "PREPAID";
        })}
      />
    </motion.div>
  );
};
